import { API_BASE_URL } from '@/config';

export const AUTH_EXPIRED_EVENT = 'auth:expired';

let refreshPromise: Promise<string | null> | null = null;
let authExpired = false;

export class AuthExpiredError extends Error {
  status: number;
  code: string;

  constructor(message: string = 'Session expired. Sign in again to continue.') {
    super(message);
    this.name = 'AuthExpiredError';
    this.status = 401;
    this.code = 'AUTH_EXPIRED';
  }
}

function getStoredAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return localStorage.getItem('access_token');
}

function getStoredRefreshToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return localStorage.getItem('refresh_token');
}

export function clearStoredTokens(): void {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

export function storeAccessToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('access_token', token);
  }

  clearAuthExpiredState();
}

export function storeRefreshToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('refresh_token', token);
  }

  clearAuthExpiredState();
}

export function clearAuthExpiredState(): void {
  authExpired = false;
}

export function isAuthExpired(): boolean {
  return authExpired;
}

export function isAuthExpiredError(error: unknown): error is AuthExpiredError {
  return error instanceof AuthExpiredError || (error as { code?: string } | null)?.code === 'AUTH_EXPIRED';
}

function notifyAuthExpired(reason: string): void {
  clearStoredTokens();

  if (authExpired) {
    return;
  }

  authExpired = true;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, {
      detail: {
        reason,
        timestamp: Date.now(),
      },
    }));
  }
}

async function parseRefreshResponse(response: Response): Promise<string | null> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }

  const data = await response.json().catch(() => null);
  const accessToken = typeof data?.access_token === 'string' ? data.access_token : null;

  if (accessToken) {
    storeAccessToken(accessToken);
  }

  return accessToken;
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      notifyAuthExpired('missing-refresh-token');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        notifyAuthExpired(`refresh-failed-${response.status}`);
        return null;
      }

      const accessToken = await parseRefreshResponse(response);
      if (!accessToken) {
        notifyAuthExpired('refresh-response-missing-access-token');
      }
      return accessToken;
    } catch {
      return null;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function getFreshAccessToken(): Promise<string | null> {
  const storedToken = getStoredAccessToken();
  if (storedToken) {
    return storedToken;
  }

  const refreshedToken = await refreshAccessToken();
  return refreshedToken ?? getStoredAccessToken();
}

function withAuthorizationHeader(
  options: RequestInit = {},
  token?: string | null,
  overwriteAuthorization: boolean = false
): RequestInit {
  const headers = new Headers(options.headers);

  if (token && (overwriteAuthorization || !headers.has('Authorization'))) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return {
    credentials: options.credentials ?? 'include',
    ...options,
    headers,
  };
}

export async function authenticatedFetch(
  input: string,
  options: RequestInit = {},
  retryOnUnauthorized: boolean = true
): Promise<Response> {
  if (isAuthExpired()) {
    throw new AuthExpiredError();
  }

  const initialToken = getStoredAccessToken();
  const initialOptions = withAuthorizationHeader(options, initialToken);
  const initialResponse = await fetch(input, initialOptions);

  if (!retryOnUnauthorized || initialResponse.status !== 401) {
    return initialResponse;
  }

  const refreshedToken = await refreshAccessToken();
  if (!refreshedToken) {
    notifyAuthExpired('request-unauthorized-after-refresh');
    throw new AuthExpiredError();
  }

  const retryOptions = withAuthorizationHeader(options, refreshedToken, true);
  const retryResponse = await fetch(input, retryOptions);

  if (retryResponse.status === 401) {
    notifyAuthExpired('request-still-unauthorized-after-refresh');
    throw new AuthExpiredError();
  }

  return retryResponse;
}
